import { Stack, Pivot, PivotItem } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import styles from "./AnalysisPanel.module.css";

import { SupportingContent } from "../SupportingContent";
import { ChatAppResponse } from "../../api";
import { AnalysisPanelTabs } from "./AnalysisPanelTabs";
import { ThoughtProcess } from "./ThoughtProcess";
import { MarkdownViewer } from "../MarkdownViewer";
import { PdfViewer } from "../PdfViewer";
import { useMsal } from "@azure/msal-react";
import { getHeaders } from "../../api";
import { useLogin, getToken } from "../../authConfig";
import { useState, useEffect, useRef } from "react";
import CloseIcon from "../../assets/Close.png";
import sendIcon from "../../assets/SendIcon.png";

interface Props {
    className: string;
    activeTab: AnalysisPanelTabs;
    onActiveTabChanged: (tab: AnalysisPanelTabs) => void;
    activeCitation: string | undefined;
    citationHeight: string;
    answer: ChatAppResponse;
    onClose?: () => void;
}

const pivotItemDisabledStyle = { disabled: true, style: { color: "grey" } };

export const AnalysisPanel = ({ answer, activeTab, activeCitation, citationHeight, className, onActiveTabChanged, onClose }: Props) => {
    const isDisabledThoughtProcessTab: boolean = !answer.context.thoughts;
    const isDisabledSupportingContentTab: boolean = !answer.context.data_points;
    const isDisabledCitationTab: boolean = !activeCitation;
    const [citation, setCitation] = useState("");
    const [citationError, setCitationError] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [pdfContainerWidth, setPdfContainerWidth] = useState(594);
    const [initialPage, setInitialPage] = useState<number | undefined>(undefined);
    const contentEndRef = useRef<HTMLDivElement>(null);
    const pdfContainerRef = useRef<HTMLDivElement>(null);

    const client = useLogin ? useMsal().instance : undefined;
    const { t } = useTranslation();

    const scrollToBottom = () => {
        contentEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Detect mobile device
    useEffect(() => {
        const checkMobile = () => {
            const userAgent = navigator.userAgent.toLowerCase();
            const mobileKeywords = ["mobile", "android", "iphone", "ipad", "ipod", "blackberry", "windows phone"];

            // Multiple detection methods for better reliability
            const isUserAgentMobile = mobileKeywords.some(keyword => userAgent.includes(keyword));
            const isScreenSmall = window.innerWidth <= 768;
            const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
            const isPortrait = window.innerHeight > window.innerWidth;

            // More comprehensive mobile detection
            const isMobileDevice = isUserAgentMobile || (isScreenSmall && isTouchDevice) || (isScreenSmall && isPortrait);

            setIsMobile(isMobileDevice);
        };

        // Check immediately and after a small delay to ensure DOM is ready
        checkMobile();
        setTimeout(checkMobile, 100);

        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    // Observe PDF container width for responsive PdfViewer
    useEffect(() => {
        const el = pdfContainerRef.current;
        if (!el) return;
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                const width = entry.contentRect.width;
                if (width > 0) {
                    setPdfContainerWidth(Math.floor(width));
                }
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const fetchCitation = async () => {
        const token = client ? await getToken(client) : undefined;
        if (activeCitation) {
            setCitationError(false);
            setCitation("");
            setInitialPage(undefined);
            // Get hash from the URL as it may contain #page=N
            // which helps browser PDF renderer jump to correct page N
            const originalHash = activeCitation.indexOf("#") ? activeCitation.split("#")[1] : "";
            
            // Extract page number from hash (e.g., "page=22" -> 22)
            if (originalHash) {
                const pageMatch = originalHash.match(/page=(\d+)/);
                if (pageMatch) {
                    setInitialPage(parseInt(pageMatch[1], 10));
                }
            }
            
            try {
                const response = await fetch(activeCitation, {
                    method: "GET",
                    headers: await getHeaders(token)
                });
                if (!response.ok) {
                    console.error("Citation fetch failed:", response.status, response.statusText);
                    setCitationError(true);
                    return;
                }
                const citationContent = await response.blob();
                let citationObjectUrl = URL.createObjectURL(citationContent);
                // Add hash back to the new blob URL
                if (originalHash) {
                    citationObjectUrl += "#" + originalHash;
                }
                setCitation(citationObjectUrl);
            } catch (error) {
                console.error("Citation fetch error:", error);
                setCitationError(true);
            }
        }
    };
    useEffect(() => {
        fetchCitation();
    }, [activeCitation]);

    const renderFileViewer = () => {
        if (!activeCitation) {
            return null;
        }

        if (citationError) {
            return (
                <div style={{ padding: "20px", textAlign: "center", color: "#c00" }}>
                    {t("citation.error", "Failed to load citation")}
                </div>
            );
        }

        if (!citation) {
            return (
                <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                    {t("citation.loading", "Loading citation...")}
                </div>
            );
        }

        // Extract file extension, handling URLs with hash fragments like #page=11
        const urlWithoutHash = activeCitation.split("#")[0];
        const fileExtension = urlWithoutHash.split(".").pop()?.toLowerCase();

        switch (fileExtension) {
            case "png":
                return <img src={citation} className={styles.citationImg} alt="Citation Image" />;
            case "md":
                return <MarkdownViewer src={activeCitation} />;
            case "pdf":
                if (isMobile) {
                    return (
                        <div className={styles.mobilePdfContainer}>
                            <div className={styles.mobilePdfHeader}>
                                <h3>{t("citation.pdfDocument")}</h3>
                                <p>{t("citation.mobileOptimizedView")}</p>
                            </div>
                            <div className={styles.mobilePdfActions}>
                                <a href={citation} target="_blank" rel="noopener noreferrer" className={styles.pdfActionButton}>
                                    {t("citation.openInNewTab")} ↗
                                </a>
                                <a href={citation} download className={styles.pdfActionButton}>
                                    {t("citation.downloadPdf")} ⬇
                                </a>
                            </div>
                            <div className={styles.mobilePdfPreview}>
                                <iframe title="Citation" src={citation} width="100%" height="400px" className={styles.mobilePdfIframe} />
                            </div>
                        </div>
                    );
                }
                return <PdfViewer src={citation} width={pdfContainerWidth} height={"calc(100vh - 165px)"} initialPage={initialPage} />;
            default:
                return <PdfViewer src={citation} width={pdfContainerWidth} height={"calc(100vh - 165px)"} initialPage={initialPage} />;
        }
    };

    return (
        <div className={`${className} ${styles.analysisContainer}`}>
            {onClose && (
                <button
                    title={t("buttons.close", "Close")}
                    aria-label={t("buttons.close", "Close")}
                    onClick={onClose}
                    className={styles.closeButton}
                >
                    <img src={CloseIcon} alt="Close" className={styles.closeIcon} />
                </button>
            )}
            <div className={activeTab === AnalysisPanelTabs.CitationTab ? styles.tabAccentLineCitation : styles.tabAccentLine}></div>
            <Pivot selectedKey={activeTab} onLinkClick={pivotItem => pivotItem && onActiveTabChanged(pivotItem.props.itemKey! as AnalysisPanelTabs)}>
                {/* <PivotItem
                    itemKey={AnalysisPanelTabs.ThoughtProcessTab}
                    headerText={t("headerTexts.thoughtProcess")}
                    headerButtonProps={isDisabledThoughtProcessTab ? pivotItemDisabledStyle : undefined}
                >
                    <ThoughtProcess thoughts={answer.context.thoughts || []} />
                </PivotItem> */}
                <PivotItem
                    itemKey={AnalysisPanelTabs.SupportingContentTab}
                    headerText={t("headerTexts.supportingContent")}
                    headerButtonProps={isDisabledSupportingContentTab ? pivotItemDisabledStyle : undefined}
                >
                    <SupportingContent supportingContent={answer.context.data_points} />
                </PivotItem>
                <PivotItem
                    itemKey={AnalysisPanelTabs.CitationTab}
                    headerText={t("headerTexts.citation")}
                    headerButtonProps={isDisabledCitationTab ? pivotItemDisabledStyle : undefined}
                >
                    <div ref={pdfContainerRef} style={{ width: "100%" }}>
                        {renderFileViewer()}
                    </div>
                </PivotItem>
            </Pivot>
            <div ref={contentEndRef} />
        </div>
    );
};
